import {isFeatureEnabled} from '@github-ui/feature-flags'
import {BetaLabel} from '@github-ui/lifecycle-labels/beta'
import {noop} from '@github-ui/noop'
import {Link, useLocation} from '@github-ui/react-router'
import {SidebarList} from '@github-ui/shared-dashboard-sidebar/SidebarList'
import {softNavigate} from '@github-ui/soft-navigate'
import {isStaff} from '@github-ui/stats'
import {useAnalytics} from '@github-ui/use-analytics'
import {MilestoneIcon, StackIcon, TableIcon, TagIcon} from '@primer/octicons-react'
import {NavList} from '@primer/react'
import type {ComponentType, MouseEvent, ReactNode} from 'react'
import {useCallback, useEffect, useMemo, useRef} from 'react'

import {repoSidebarLinks} from './repos-issues-sidebar-links'
import {useIssuesSidebarState} from './use-issues-sidebar-state'
import {getRepoIssuesQueryPath} from './urls'
import styles from './ReposIssuesSidebar.module.css'

const PUBLIC_FEEDBACK_URL = 'https://gh.io/issues-sidebar-feedback'
const STAFF_FEEDBACK_URL = 'https://gh.io/issues-sidebar-feedback-staff'

/** Shared analytics identifier for the repo-scoped issues sidebar. */
const DASHBOARD_TYPE = 'repo-issues'

/**
 * NavList.Item uses polymorphic `as` prop typing, which means TypeScript cannot
 * accept a spread of `{href} | {as, to}` — it sees a union and can't narrow it
 * at the spread site. This wrapper centralises the ternary so each call site
 * stays clean. See SidebarList in shared-dashboard-sidebar for the same pattern.
 *
 * NavList.LeadingVisual must also be a direct child of NavList.Item, so icons
 * are rendered inside this wrapper rather than passed as children.
 */
function SidebarNavItem({
  path,
  forceHardNavigation,
  'aria-current': ariaCurrent,
  icon: Icon,
  onClick,
  children,
}: {
  path: string
  forceHardNavigation: boolean
  'aria-current': false | 'page'
  icon: ComponentType
  onClick?: () => void
  children: ReactNode
}) {
  const handleClick = useCallback(
    (e: MouseEvent) => {
      onClick?.()
      e.preventDefault()
      softNavigate(path)
    },
    [path, onClick],
  )

  const handleSoftClick = useCallback(() => {
    onClick?.()
  }, [onClick])

  if (forceHardNavigation) {
    return (
      <NavList.Item aria-current={ariaCurrent} href={path} onClick={handleClick}>
        <NavList.LeadingVisual>
          <Icon />
        </NavList.LeadingVisual>
        {children}
      </NavList.Item>
    )
  }
  return (
    <NavList.Item aria-current={ariaCurrent} as={Link} to={path} onClick={handleSoftClick}>
      <NavList.LeadingVisual>
        <Icon />
      </NavList.LeadingVisual>
      {children}
    </NavList.Item>
  )
}

export interface ReposIssuesSidebarProps {
  owner: string
  repo: string
  forceHardNavigation?: boolean
}

export function ReposIssuesSidebar({owner, repo, forceHardNavigation = false}: ReposIssuesSidebarProps) {
  const {pathname} = useLocation()
  const {selectedLink} = useIssuesSidebarState()
  const repoPath = `/${owner}/${repo}`
  const labelsPath = `${repoPath}/labels`
  const issuesPath = `${repoPath}/issues`
  const projectsPath = `${repoPath}/projects`
  const milestonesPath = `${repoPath}/milestones`
  const milestonePath = `${repoPath}/milestone/`
  const viewsPath = `${issuesPath}/views`

  // Track sidebar impressions so we can measure the ratio of users who
  // see the sidebar vs. those who click a link.
  const {sendAnalyticsEvent} = useAnalytics()
  const impressionSentRef = useRef(false)
  useEffect(() => {
    if (!impressionSentRef.current) {
      impressionSentRef.current = true
      sendAnalyticsEvent(`${DASHBOARD_TYPE}.view`, 'SIDEBAR_IMPRESSION')
    }
  }, [sendAnalyticsEvent])

  const handleNavClick = useCallback(
    (id: string) => () => {
      sendAnalyticsEvent(`${DASHBOARD_TYPE}.click`, 'SIDEBAR_LINK', {id})
    },
    [sendAnalyticsEvent],
  )

  const linksWithPaths = useMemo(
    () =>
      repoSidebarLinks.map(link => ({
        ...link,
        path: getRepoIssuesQueryPath({owner, repo, query: link.query}),
      })),
    [owner, repo],
  )

  return (
    <aside aria-label="Repository navigation" className={styles.sidebar}>
      <SidebarList
        dashboardType={DASHBOARD_TYPE}
        links={linksWithPaths}
        selectedLink={selectedLink}
        onSelectLink={noop}
        generateLinkPath={path => path ?? issuesPath}
        forceHardNavigation={forceHardNavigation}
      />

      <hr className={styles.divider} />

      <NavList aria-label="Pages">
        {/* Projects is a Rails page — always hard-navigate to avoid soft-navigating to a route the SPA cannot handle. */}
        <SidebarNavItem
          aria-current={pathname === projectsPath && 'page'}
          path={projectsPath}
          forceHardNavigation
          icon={TableIcon}
          onClick={handleNavClick('projects')}
        >
          Projects
        </SidebarNavItem>
        {isFeatureEnabled('issue_pinned_views_index_page') && (
          <SidebarNavItem
            aria-current={pathname === viewsPath && 'page'}
            path={viewsPath}
            forceHardNavigation={forceHardNavigation}
            icon={StackIcon}
            onClick={handleNavClick('views')}
          >
            Views
          </SidebarNavItem>
        )}
        <SidebarNavItem
          aria-current={(pathname.startsWith(milestonesPath) || pathname.startsWith(milestonePath)) && 'page'}
          path={milestonesPath}
          forceHardNavigation={forceHardNavigation}
          icon={MilestoneIcon}
          onClick={handleNavClick('milestones')}
        >
          Milestones
        </SidebarNavItem>
        <SidebarNavItem
          aria-current={(pathname === labelsPath || pathname === `${issuesPath}/labels`) && 'page'}
          path={labelsPath}
          forceHardNavigation={forceHardNavigation}
          icon={TagIcon}
          onClick={handleNavClick('labels')}
        >
          Labels
        </SidebarNavItem>
      </NavList>

      <BetaLabel className={styles.betaLabel} feedbackUrl={isStaff() ? STAFF_FEEDBACK_URL : PUBLIC_FEEDBACK_URL} />
    </aside>
  )
}
